import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import {
  AnalyticsReportsPageDocument,
  AnalyticsReportsPageQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import DeleteReportDialog from 'modules/ai_reports/components/DeleteReportDialog'
import { Layout } from 'modules/ai_reports/components/Layout'
import { NewExplorationModal } from 'modules/ai_reports/components/NewExplorationModal'

const DEFAULT_SORTING = { field: 'updatedAt', order: 'desc' } as const

type ReportNode = AnalyticsReportsPageQuery['analytics']['reportsConnection']['nodes'][number]

const ReportsListSection = () => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const [itemToDelete, setItemToDelete] = useState<ReportNode | null>(null)

  const [newExplorationDialogOpen, setNewExplorationDialogOpen] = useState(false)

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: (count: number) =>
      count !== undefined ? i18n.t('ai_reports.reports.count', { count }) : '',
    document: AnalyticsReportsPageDocument,
    mapVariables: ({ search, sortings, filters }) => ({
      sortOrder: sortings?.[0] ?? DEFAULT_SORTING,
      search: search,
      name: filters.name,
      companyId: company.id,
    }),
    mapData: (data: AnalyticsReportsPageQuery) => data.analytics.reportsConnection.nodes,
    mapPagination: (data: AnalyticsReportsPageQuery) => ({
      total: data.analytics.reportsConnection.totalCount ?? 0,
    }),
    itemUrl: (item: ReportNode) =>
      navigation.aiReports.reports.reportId.fullPath({ reportId: item.id }),
    search: { enabled: true },
    defaultSorting: DEFAULT_SORTING,
    sortings: {
      name: { label: i18n.t('ai_reports.reports.name') },
      authorId: { label: i18n.t('ai_reports.reports.created_by') },
      createdAt: { label: i18n.t('ai_reports.reports.created_at') },
      updatedAt: { label: i18n.t('ai_reports.reports.last_update') },
    },
    paginationType: 'pages',
    perPage: 15,
    itemActions: (item: ReportNode) => [
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => setItemToDelete(item),
      },
    ],
    primaryActions: () => ({
      label: i18n.t('ai_reports.reports.new_exploration'),
      icon: Add,
      onClick: () => {
        setNewExplorationDialogOpen(true)
      },
    }),
  })

  return (
    <Layout paddingTop="s16">
      <DeleteReportDialog itemToDelete={itemToDelete} setItemToDelete={setItemToDelete} />
      <NewExplorationModal
        open={newExplorationDialogOpen}
        onOpenChange={setNewExplorationDialogOpen}
      />
      <OneDataCollection
        source={source}
        fullHeight
        emptyStates={{
          'no-data': {
            emoji: '📈',
            title: i18n.t('ai_reports.reports.empty_state.no_data.title'),
            description: i18n.t('ai_reports.reports.empty_state.no_data.description'),
            // TODO: Enable when Guidance is ready
            // actions: [
            //   {
            //     label: i18n.t('ai_reports.reports.empty_state.no_data.action'),
            //     onClick: () => {
            //       // TODO
            //       // navigate(navigation.aiReports.reports.new.fullPath())
            //     },
            //   },
            // ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('ai_reports.reports.name'),
                  sorting: 'name',
                  render: (item: ReportNode) => ({ type: 'text', value: item.name }),
                },
                {
                  label: i18n.t('ai_reports.reports.datasource'),
                  render: (item: ReportNode) => {
                    return {
                      type: 'tag',
                      value: {
                        type: 'raw',
                        label: item.datasource?.name,
                      },
                    }
                  },
                  width: 300,
                },
                {
                  label: i18n.t('ai_reports.reports.description'),
                  render: (item: ReportNode) => ({
                    type: 'longText',
                    value: {
                      text: item.description,
                      lines: 2,
                    },
                  }),
                  width: 300,
                },
                {
                  label: i18n.t('ai_reports.reports.created_at'),
                  sorting: 'createdAt',
                  render: (item: ReportNode) => ({
                    type: 'date',
                    value: new Date(item.createdAt),
                  }),
                },
                {
                  label: i18n.t('ai_reports.reports.last_update'),
                  sorting: 'updatedAt',
                  render: (item: ReportNode) => ({
                    type: 'date',
                    value: new Date(item.updatedAt),
                  }),
                },
                {
                  label: i18n.t('ai_reports.reports.created_by'),
                  sorting: 'authorId',
                  render: (item: ReportNode) => {
                    if (!item.author) return '-'
                    return {
                      type: 'person',
                      value: { ...item.author, src: buildUrl(item.author.avatar?.url) },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
    </Layout>
  )
}

export default ReportsListSection
